import { baseURL, clientApi } from "@/utils/api";
import {
  TGetQueryTalentListReqDto,
  TGetTalentFilterOptionsResDto,
  TGetTalentListReqDto,
  TGetTalentListResDto,
  TTalentListOutputDto,
} from "@/api/services/main/talent/dtos/get-talent-list.dto";
import { useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { TDeleteTalentResDto } from "@/api/services/main/talent/dtos/delete-talent.dto";
import { TCreateTalentReqDto, TCreateTalentResDto } from "@/api/services/main/talent/dtos/create-talent-req.dto";
import { useParams } from "react-router-dom";
import { TGetTalentResDto } from "@/api/services/main/talent/dtos/get-talent.dto";
import { TUpdateTalentReqDto, TUpdateTalentResDto } from "@/api/services/main/talent/dtos/update-talent-req.dto";
import {
  TUpdateTalentFromCaseMatchReqDto,
  TUpdateTalentFromCaseMatchResDto,
} from "@/api/services/main/talent/dtos/update-talent-from-case-match.dto";
import { TCreateTalentCommentReqDto, TCreateTalentCommentResDto } from "@/api/services/main/talent/dtos/create-talent-comment.dto";
import { TGetTalentCommentsResDto } from "@/api/services/main/talent/dtos/get-talent-comment.dto";
import { TDeleteTalentCommentResDto } from "@/api/services/main/talent/dtos/delete-talent-comment.dto";
import { TGetTalentExistsResDto } from "@/api/services/main/talent/dtos/get-talent-exists.dto";
import { TCheckPassportDuplicateResDto } from "@/api/services/main/talent/dtos/check-passport-duplicate.dto";
import { createUrlWithParams } from "@/utils/url";
import { TTalentRowExcel } from "@/features/talent/types/talentExcel";
import { useMemo } from "react";
import { TGenerateScanImageResDto } from "@/api/services/main/talent/dtos/generate-scan-image.dto";
import { authTokenManager } from "@/services/auth-token-manager";
import { CACHED_TIME } from "@/constants/common";
import {
  TGetTalentIdsWithPagesReqDto,
  TGetTalentIdsWithPagesResDto,
} from "@/api/services/main/talent/dtos/get-talent-ids-with-pages.dto";

const processDataBeforeCallApi = (payload: TCreateTalentReqDto) => {
  const formData = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null) return formData.append(key, "");
    if (Array.isArray(value) && value.length === 0) return formData.append(key, "");

    if (value instanceof File) {
      formData.append(key, value);
    } else if (Array.isArray(value)) {
      if (value.every((v) => v instanceof File)) {
        value.forEach((file) => formData.append(key, file));
      } else {
        formData.append(key, JSON.stringify(value));
      }
    } else if (typeof value === "number") {
      formData.append(key, JSON.stringify(value));
    } else {
      formData.append(key, String(value));
    }
  });
  return formData;
};

export const fetchTalentFilterOptionsApi = async (): Promise<TGetTalentFilterOptionsResDto> => {
  const { data } = await clientApi.get<TGetTalentFilterOptionsResDto>("/talents/filter-options");
  return data;
};

export const useTalentFilterOptions = () => {
  const { data, ...rest } = useQuery({
    queryKey: [QUERY_KEYS.TALENT.FETCH_FILTER_OPTIONS],
    queryFn: () => fetchTalentFilterOptionsApi(),
    staleTime: CACHED_TIME,
  });

  const talentNumberOptions = useMemo(() => (data?.data?.talentNumbers ?? []).map((v) => ({ label: v, value: v })), [data]);
  const fullNameOptions = useMemo(() => (data?.data?.fullNames ?? []).map((v) => ({ label: v, value: v })), [data]);
  const nickNameOptions = useMemo(() => (data?.data?.nickNames ?? []).map((v) => ({ label: v, value: v })), [data]);
  const desiredWorkLocationOptions = useMemo(() => (data?.data?.desiredWorkLocations ?? []).map((v) => ({ label: v, value: v })), [data]);

  return { talentNumberOptions, fullNameOptions, nickNameOptions, desiredWorkLocationOptions, ...rest };
};

export const getTalentListApi = async (params: TGetTalentListReqDto): Promise<TGetTalentListResDto> => {
  const { data } = await clientApi.get<TGetTalentListResDto>("/talents", { params });
  return data;
};

export const useTalentList = (params: TGetTalentListReqDto, enabled?: boolean) => {
  return useQuery({
    queryKey: [QUERY_KEYS.TALENT.FETCH_LIST, params],
    queryFn: () => getTalentListApi(params),
    enabled: enabled !== false,
  });
};

const TALENT_OPTIONS_PER_PAGE = 500;

const fetchAllTalentsForDuplicateCheck = async () => {
  const first = await getTalentListApi({ page: 1, perPage: TALENT_OPTIONS_PER_PAGE });
  const items = [...first.data];
  const totalPage = first.pagination?.totalPage ?? 1;

  for (let page = 2; page <= totalPage; page++) {
    const next = await getTalentListApi({ page, perPage: TALENT_OPTIONS_PER_PAGE });
    items.push(...next.data);
  }

  return items;
};

export const useTalentOptions = () => {
  const { data, ...rest } = useQuery({
    queryKey: [QUERY_KEYS.TALENT.FETCH_LIST, "duplicate-check-all"],
    queryFn: fetchAllTalentsForDuplicateCheck,
  });
  const options = useMemo(
    () =>
      data
        ? data.map((item) => ({
            fullName: item.fullName,
            nickName: item.nickName,
            birthday: item.birthday,
          }))
        : [],
    [data],
  );
  return {
    options,
    ...rest,
  };
};

export const deleteTalentApi = async (ids: number[], isAll = false, filter: TGetQueryTalentListReqDto = {}): Promise<TDeleteTalentResDto> => {
  const { data } = await clientApi.delete<TDeleteTalentResDto>("/talents", {
    data: {
      talentIds: ids || [],
      isAll,
      ...filter,
    },
  });
  return data;
};

export const createTalentApi = async (payload: TCreateTalentReqDto): Promise<TCreateTalentResDto> => {
  const formData = processDataBeforeCallApi(payload);
  const rs = await clientApi.post<TCreateTalentResDto>("/talents", formData);
  return rs.data;
};

export const fetchTalentInfoApi = async (id: string | number): Promise<TGetTalentResDto> => {
  const { data } = await clientApi.get<TGetTalentResDto>(`/talents/${id}`);
  return data;
};

export const useTalentInfo = (id?: number) => {
  const { talentId } = useParams();

  const fetchId = id ?? talentId;

  const isValidId = useMemo(() => {
    if (!fetchId) return false;
    const idNumber = Number(fetchId);
    return !isNaN(idNumber) && Number.isInteger(idNumber) && idNumber > 0;
  }, [fetchId]);

  return useQuery({
    queryKey: [QUERY_KEYS.TALENT.FETCH_DETAIL, fetchId],
    queryFn: async () => {
      return fetchId ? await fetchTalentInfoApi(fetchId) : undefined;
    },
    enabled: isValidId,
  });
};

export const updateTalentApi = async (id: string | number, payload: TUpdateTalentReqDto): Promise<TUpdateTalentResDto> => {
  const formData = processDataBeforeCallApi(payload);
  const rs = await clientApi.put<TUpdateTalentResDto>(`/talents/${id}`, formData);
  return rs.data;
};

/**
 * Update talent from case match edit modal
 * This API only updates specific fields: personInChargeId, majorId, jobTypeId, languageCertificates, and files
 */
export const updateTalentFromCaseMatchApi = async (
  id: string | number,
  payload: TUpdateTalentFromCaseMatchReqDto,
): Promise<TUpdateTalentFromCaseMatchResDto> => {
  const formData = new FormData();

  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined) return;

    if (value === null) {
      formData.append(key, "");
      return;
    }

    if (Array.isArray(value)) {
      if (value.every((v) => v instanceof File)) {
        value.forEach((file) => formData.append(key, file));
      } else {
        formData.append(key, JSON.stringify(value));
      }
    } else if (typeof value === "number") {
      formData.append(key, JSON.stringify(value));
    } else {
      formData.append(key, String(value));
    }
  });

  const rs = await clientApi.patch<TUpdateTalentFromCaseMatchResDto>(`/talents/${id}/from-case-match`, formData);
  return rs.data;
};

export const createTalentCommentApi = async (talentId: number, payload: TCreateTalentCommentReqDto): Promise<TCreateTalentCommentResDto> => {
  const rs = await clientApi.post<TCreateTalentCommentResDto>(`/talents/${talentId}/comments`, payload);
  return rs.data;
};

export const deleteTalentCommentApi = async (talentId: number, commentId: number): Promise<TDeleteTalentCommentResDto> => {
  const rs = await clientApi.delete<TDeleteTalentCommentResDto>(`/talents/${talentId}/comments/${commentId}`);
  return rs.data;
};

export const fetchTalentCommentsApi = async (id: string | number): Promise<TGetTalentCommentsResDto> => {
  const { data } = await clientApi.get<TGetTalentCommentsResDto>(`/talents/${id}/comments`);
  return data;
};

export const useTalentCommentsList = (id?: number) => {
  const { talentId } = useParams();

  const fetchId = id ?? talentId;
  return useQuery({
    queryKey: [QUERY_KEYS.TALENT_COMMENT.FETCH_LIST, fetchId],
    queryFn: async () => {
      return fetchId ? await fetchTalentCommentsApi(fetchId) : undefined;
    },
    enabled: Boolean(fetchId),
  });
};

export const fetchTalentExistsApi = async (id: number | null, fullName: string, birthday?: string | null): Promise<TGetTalentExistsResDto> => {
  const params = { fullName, ...(birthday && { birthday }), ...(id && { id }) };
  const url = createUrlWithParams("/talents/exists", params);

  const { data } = await clientApi.get<TGetTalentExistsResDto>(url);

  return data;
};

export const fetchCheckPassportDuplicateApi = async (id: number | undefined, passportNumber: string): Promise<TCheckPassportDuplicateResDto> => {
  const params = { passportNumber, ...(id && { id }) };
  const url = createUrlWithParams("/talents/check-passport-duplicate", params);

  const { data } = await clientApi.get<TCheckPassportDuplicateResDto>(url);

  return data;
};

export const validateImportTalentApi = async (payload: TTalentRowExcel[]) => {
  const rs = await clientApi.post("/talents/validate-import", payload);
  return rs.data;
};

export const importTalentsApi = async (payload: TTalentListOutputDto[]) => {
  const rs = await clientApi.post("/talents/import", payload);
  return rs.data;
};

export const exportTalentApi = async (ids: number[], isAll = false, filter: TGetQueryTalentListReqDto = {}) => {
  return await clientApi.get("/talents/export", {
    params: {
      talentIds: ids || [],
      isAll: Number(isAll),
      ...filter,
    },
    responseType: "blob",
  });
};

export type TGenerateNoteStreamChunk = {
  progress?: number;
  data?: unknown;
  error?: string;
};

export const generateNoteApi = async (
  memo: string | Record<string, string | string[]>,
  onProgress?: (progress: number) => void,
  signal?: AbortSignal,
): Promise<{ data: TGenerateScanImageResDto | null }> => {
  const authHeader = clientApi.defaults.headers.common.Authorization as string | undefined;

  const response = await fetch(`${baseURL}/talents/generate-note`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(authHeader && { Authorization: authHeader }),
    },
    body: JSON.stringify({ memo }),
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = `HTTP error! status: ${response.status}`;
    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.message || errorMessage;
    } catch {
      // Use default error message
    }
    throw new Error(errorMessage);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error("ReadableStream not supported");
  }

  const decoder = new TextDecoder();
  let resultData: TGenerateScanImageResDto | null = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    const chunk = decoder.decode(value, { stream: true });
    const lines = chunk.split("\n").filter((line) => line.trim());

    for (const line of lines) {
      try {
        const parsed: TGenerateNoteStreamChunk = JSON.parse(line);

        if (parsed.progress !== undefined && onProgress) {
          onProgress(parsed.progress);
        }

        if (parsed.data) {
          resultData = parsed.data as TGenerateScanImageResDto;
        }

        if (parsed.error) {
          throw new Error(parsed.error);
        }
      } catch (e) {
        if (e instanceof SyntaxError) continue;
        throw e;
      }
    }
  }

  return { data: resultData };
};

export type TGenerateScanImageStreamChunk = {
  progress?: number;
  /** Per-file OCR event metadata (IROHANA_MATCH_DEV-280 unified scan). */
  currentFile?: string;
  fileIndex?: number;
  totalFiles?: number;
  data?: unknown;
  error?: string;
};

/**
 * Stream the unified talent scan endpoint and forward progress + per-file
 * metadata. fileType arg removed (IROHANA_MATCH_DEV-280): the BE now
 * classifies card / resume / note from a single batch.
 */
export const generateScanImageTalentApi = async (
  files: File[],
  memo: string | Record<string, string | string[]>,
  onProgress?: (chunk: TGenerateScanImageStreamChunk) => void,
  signal?: AbortSignal,
): Promise<{ data: unknown }> => {
  const makeFetchRequest = async (authToken: string | null) => {
    const formData = new FormData();

    files.forEach((file) => {
      formData.append("files", file);
    });
    formData.append("memo", JSON.stringify(memo));

    const response = await fetch(`${baseURL}/talents/generate-scan-image`, {
      method: "POST",
      headers: {
        ...(authToken && { Authorization: `Bearer ${authToken}` }),
      },
      body: formData,
      signal,
    });

    return response;
  };

  const processStreamingResponse = async (response: Response) => {
    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error("ReadableStream not supported");
    }

    const decoder = new TextDecoder();
    let resultData: unknown = null;
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      // Accumulate partial chunks to handle large JSON split across reads
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      // Keep last incomplete line in buffer for next iteration
      buffer = lines.pop() ?? "";

      for (const line of lines) {
        if (!line.trim()) continue;
        try {
          const parsed: TGenerateScanImageStreamChunk = JSON.parse(line);

          if (parsed.progress !== undefined && onProgress) {
            onProgress(parsed);
          }

          if (parsed.data !== undefined) {
            resultData = parsed.data;
          }

          if (parsed.error) {
            throw new Error(parsed.error);
          }
        } catch (e) {
          if (e instanceof SyntaxError) continue;
          throw e;
        }
      }
    }

    return { data: resultData };
  };

  const authToken = authTokenManager.getToken();
  let response = await makeFetchRequest(authToken);

  if (response.status === 401) {
    const newToken = await authTokenManager.refreshToken();
    if (!newToken) {
      throw new Error("Authentication failed. Please login again.");
    }
    response = await makeFetchRequest(newToken);
  }

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = `HTTP error! status: ${response.status}`;
    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.message || errorMessage;
    } catch {
      // Use default error message
    }
    throw new Error(errorMessage);
  }

  return await processStreamingResponse(response);
};

export const downloadSingleResumeApi = async (talentId: number, masked?: boolean) => {
  return await clientApi.get(`/talents/${talentId}/download-resume`, {
    responseType: "blob",
    params: masked ? { masked: true } : undefined,
  });
};

export const getTalentIdsWithPagesApi = async (payload: TGetTalentIdsWithPagesReqDto): Promise<TGetTalentIdsWithPagesResDto> => {
  const { data } = await clientApi.post<TGetTalentIdsWithPagesResDto>("/talents/ids-with-pages", payload);
  return data;
};
